import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, MessageSquare, Calendar, Clock, Tag, 
  Star, TrendingUp, Download, Plus, Trash2, CheckCircle, 
  AlertCircle, ChevronRight, X, Edit2, Send, PhoneCall, 
  Video, FileText, BarChart3, Filter, Search, RefreshCw,
  ExternalLink, Flame, Building2, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===== Lead heat tier (derived from lead_score 0-100) =====
const getHeatTier = (score = 0) => {
  if (score >= 70) return { id: 'hot', label: 'Hot', color: '#ef4444', emoji: '🔥' };
  if (score >= 40) return { id: 'warm', label: 'Warm', color: '#f59e0b', emoji: '☀️' };
  return { id: 'cold', label: 'Cold', color: '#60a5fa', emoji: '❄️' };
};

// ===== Predefined tag palette (admin can also type custom tags) =====
const TAG_PALETTE = [
  { label: 'VIP',                color: '#D4AF37' },
  { label: 'High Net Worth',     color: '#a855f7' },
  { label: 'Diaspora',           color: '#06b6d4' },
  { label: 'Cash Buyer',         color: '#22c55e' },
  { label: 'Mortgage Needed',    color: '#f97316' },
  { label: 'Returning Investor', color: '#ec4899' },
  { label: 'Hot Lead',           color: '#ef4444' },
  { label: 'JV Partner',         color: '#3b82f6' },
  { label: 'Mailing List',       color: '#94a3b8' },
  { label: 'Cold Call Required', color: '#64748b' },
];
const tagColor = (label) => {
  const found = TAG_PALETTE.find(t => t.label.toLowerCase() === String(label || '').toLowerCase());
  return found ? found.color : '#D4AF37';
};

// ===== Property Matching — extract preferences from lead and match against deals =====
const extractLeadPrefs = (lead) => {
  // Prefer structured fields (new leads from deal enquiry); fall back to parsing the message blob
  const prefs = {
    strategy: (lead.preferred_strategy || '').trim(),
    funds:    (lead.funds_available || '').trim(),
    timeline: (lead.invest_timeline || '').trim(),
    experience: (lead.experience_level || '').trim(),
  };
  if (!prefs.strategy && lead.message) {
    const m = lead.message.match(/Preferred Strategy:\s*(.+)/i);
    if (m) prefs.strategy = m[1].trim().replace(/^N\/A$/i, '');
  }
  if (!prefs.funds && lead.message) {
    const m = lead.message.match(/Funds Available:\s*(.+)/i);
    if (m) prefs.funds = m[1].trim().replace(/^N\/A$/i, '');
  }
  if (!prefs.timeline && lead.message) {
    const m = lead.message.match(/Timeline:\s*(.+)/i);
    if (m) prefs.timeline = m[1].trim().replace(/^N\/A$/i, '');
  }
  return prefs;
};

const parseFundsToNumber = (funds) => {
  if (!funds) return 0;
  const cleaned = String(funds).replace(/[^\d.]/g, '');
  return cleaned ? parseFloat(cleaned) : 0;
};

const matchDealsToLead = (lead, allDeals) => {
  if (!Array.isArray(allDeals) || allDeals.length === 0) return [];
  const prefs = extractLeadPrefs(lead);
  const stratLower = prefs.strategy.toLowerCase();
  const interest = (lead.investment_interest || '').toLowerCase();
  const fundsNum = parseFundsToNumber(prefs.funds);

  return allDeals.map(deal => {
    let score = 0;
    const reasons = [];

    // 1) strategy_tags overlap with preferred_strategy or investment_interest
    const dealTags = (deal.strategy_tags || []).map(t => String(t).toLowerCase());
    if (stratLower && dealTags.some(t => stratLower.includes(t) || t.includes(stratLower))) {
      score += 40; reasons.push(`Matches strategy "${prefs.strategy}"`);
    } else if (interest && dealTags.some(t => interest.includes(t) || t.includes(interest))) {
      score += 25; reasons.push('Matches investment interest');
    }

    // 2) title / asset_type keyword match against strategy
    const titleHay = ((deal.title || '') + ' ' + (deal.asset_type || '')).toLowerCase();
    if (stratLower && titleHay.includes(stratLower)) {
      score += 15;
    }

    // 3) Funds budget match — extract "from $X" from deal title, compare to lead funds
    if (fundsNum > 0) {
      const priceMatch = (deal.title || '').match(/from\s+(?:us\$|usd|gbp|£|€)\s*([\d,.]+)/i);
      if (priceMatch) {
        const entryPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
        if (entryPrice > 0 && fundsNum >= entryPrice * 0.8) {
          score += 20;
          reasons.push(`Within budget (${prefs.funds})`);
        }
      }
    }

    // 4) Deal is published — small base score for any visible deal
    if (deal.published) score += 5;

    return { deal, score, reasons };
  })
  .filter(m => m.score >= 25)
  .sort((a, b) => b.score - a.score)
  .slice(0, 4);
};

// ===== Tags editor — clickable palette + custom tag input =====
const TagsEditor = ({ tags = [], onChange }) => {
  const [customTag, setCustomTag] = useState('');
  const toggle = (label) => {
    const exists = tags.some(t => String(t).toLowerCase() === label.toLowerCase());
    const next = exists ? tags.filter(t => String(t).toLowerCase() !== label.toLowerCase()) : [...tags, label];
    onChange(next);
  };
  const addCustom = () => {
    const v = customTag.trim();
    if (!v) return;
    if (tags.some(t => String(t).toLowerCase() === v.toLowerCase())) {
      toast.info('Tag already added');
      return;
    }
    onChange([...tags, v]);
    setCustomTag('');
  };
  const remove = (label) => {
    onChange(tags.filter(t => t !== label));
  };
  return (
    <div className="space-y-3">
      {/* Active tags */}
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {tags.length === 0 && <span className="text-white/30 text-xs italic">No tags yet — pick from below or type your own.</span>}
        {tags.map((t, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md font-semibold"
            style={{ backgroundColor: `${tagColor(t)}22`, color: tagColor(t), border: `1px solid ${tagColor(t)}44` }}
            data-testid={`active-tag-${i}`}
          >
            {t}
            <button onClick={() => remove(t)} className="hover:opacity-70" title="Remove tag" data-testid={`remove-tag-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Palette */}
      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
        {TAG_PALETTE.map(p => {
          const active = tags.some(t => String(t).toLowerCase() === p.label.toLowerCase());
          return (
            <button
              key={p.label}
              onClick={() => toggle(p.label)}
              className={`text-[10px] px-2 py-0.5 rounded font-semibold transition-all ${active ? 'opacity-40' : 'hover:opacity-80'}`}
              style={{ backgroundColor: `${p.color}1a`, color: p.color, border: `1px solid ${p.color}33` }}
              data-testid={`palette-tag-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {active ? '✓ ' : '+ '}{p.label}
            </button>
          );
        })}
      </div>

      {/* Custom tag input */}
      <div className="flex gap-2">
        <input
          value={customTag}
          onChange={e => setCustomTag(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Custom tag…"
          className="flex-1 bg-[#0a1a14] border border-white/10 rounded px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-[#D4AF37]/40"
          data-testid="custom-tag-input"
        />
        <button
          onClick={addCustom}
          className="px-3 py-1.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] text-xs font-semibold hover:bg-[#D4AF37]/25 transition-colors"
          data-testid="custom-tag-add"
        >
          Add
        </button>
      </div>
    </div>
  );
};

// ===== Matching deals card — auto-suggested investments based on lead prefs =====
const MatchingDealsCard = ({ lead, allDeals }) => {
  const matches = React.useMemo(() => matchDealsToLead(lead, allDeals), [lead, allDeals]);
  const prefs = extractLeadPrefs(lead);

  return (
    <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-lg p-4" data-testid="matching-deals-card">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[#D4AF37] text-xs uppercase tracking-wide font-bold flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Matching Opportunities
        </h4>
        <span className="text-white/30 text-[10px]">{matches.length} match{matches.length !== 1 ? 'es' : ''}</span>
      </div>

      {(prefs.strategy || prefs.funds) && (
        <p className="text-white/40 text-[11px] mb-3">
          Based on{' '}
          {prefs.strategy && <span className="text-white/70">strategy: <span className="text-[#D4AF37]">{prefs.strategy}</span></span>}
          {prefs.strategy && prefs.funds && <span> · </span>}
          {prefs.funds && <span className="text-white/70">budget: <span className="text-[#D4AF37]">{prefs.funds}</span></span>}
        </p>
      )}

      {matches.length === 0 ? (
        <p className="text-white/40 text-xs italic py-2">
          {!prefs.strategy && !prefs.funds
            ? 'No strategy / budget data captured for this lead yet.'
            : 'No live deals currently match this lead\'s preferences.'}
        </p>
      ) : (
        <div className="space-y-2">
          {matches.map(({ deal, score, reasons }, i) => {
            const cleanTitle = (deal.title || '').replace(/\n/g, ' ').replace(/\s*\|\s*/g, ' · ').split(' · ')[0];
            return (
              <a
                key={i}
                href={`/deals/${deal.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-[#0a1a14]/60 border border-white/10 hover:border-[#D4AF37]/40 rounded-md p-2.5 transition-colors group"
                data-testid={`matching-deal-${i}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h5 className="text-white text-xs font-semibold leading-snug line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                    <Building2 className="w-3 h-3 inline mr-1 text-[#D4AF37]" />{cleanTitle}
                  </h5>
                  <span className="text-[10px] text-[#D4AF37] font-bold flex-shrink-0">{score}%</span>
                </div>
                {reasons.length > 0 && (
                  <p className="text-white/40 text-[10px] leading-tight">{reasons.join(' · ')}</p>
                )}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===== WhatsApp tab — opens wa.me with prefilled message so admin sends from their own
//       Black Star PI WhatsApp account. No Twilio cost, no sandbox opt-in friction.
const WhatsAppTab = ({ lead, getStoredPassword, onSent }) => {
  const [body, setBody] = useState('');
  const [phone, setPhone] = useState(lead.phone || '');
  const [sending, setSending] = useState(false);

  // Quick prefill templates
  const templates = [
    {
      label: 'Intro',
      text: `Hi ${(lead.name || '').split(' ')[0] || 'there'}, this is the team at Black Star PI. Thanks for your interest in our property investment opportunities — happy to share full details whenever you're ready. — BSPI`,
    },
    {
      label: 'Deal follow-up',
      text: `Hi ${(lead.name || '').split(' ')[0] || 'there'}, following up on the opportunity you enquired about. Let me know if a quick 10-min call this week would help. — Black Star PI`,
    },
    {
      label: 'Brochure',
      text: `Hi ${(lead.name || '').split(' ')[0] || 'there'}, here is the full investor brochure for the deal we discussed: [link]. Reply if you'd like a call to walk through the numbers. — Black Star PI`,
    },
  ];

  // Strip everything except digits to build a wa.me URL (international format, no +).
  const normalizePhone = (raw) => (raw || '').replace(/[^\d]/g, '');

  const openInWhatsApp = async () => {
    if (!body.trim()) { toast.error('Message body is empty'); return; }
    const digits = normalizePhone(phone);
    if (digits.length < 6) { toast.error('Lead phone number is invalid'); return; }
    setSending(true);
    try {
      // Log activity first so the message is preserved in the CRM even if the user
      // never returns to confirm. Activity log uses query params (per existing endpoint).
      const qs = new URLSearchParams({
        activity_type: 'whatsapp_sent',
        description: `WhatsApp drafted for ${phone}: ${body.trim().slice(0, 280)}`,
        password: getStoredPassword(),
      }).toString();
      try {
        await fetch(`${API_URL}/api/admin/submissions/${lead.id}/activity?${qs}`, { method: 'POST' });
      } catch { /* log failures are non-blocking */ }

      // Build wa.me URL — opens WhatsApp Web (desktop) or app (mobile) with prefilled chat.
      const waUrl = `https://wa.me/${digits}?text=${encodeURIComponent(body.trim())}`;
      window.open(waUrl, '_blank', 'noopener,noreferrer');

      toast.success('WhatsApp opened with the message prefilled — hit Send to deliver.');
      setBody('');
      await onSent();
    } catch (e) {
      toast.error('Could not open WhatsApp: ' + (e?.message || 'unknown'));
    }
    setSending(false);
  };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg p-3 flex items-start gap-2.5">
        <CheckCircle className="w-4 h-4 text-[#25D366] flex-shrink-0 mt-0.5" />
        <div className="text-[#a4eec1] text-xs leading-relaxed">
          Clicking <strong>Open in WhatsApp</strong> opens WhatsApp Web or the mobile app with the
          message + recipient already filled in. You then hit Send from your own Black Star PI WhatsApp
          account — no Twilio, no sandbox opt-in. The message is logged to this lead's activity feed automatically.
        </div>
      </div>

      {/* Phone */}
      <div className="bg-white/5 rounded-lg p-4 space-y-2.5">
        <label className="text-white/50 text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5" /> Recipient Phone (international format)
        </label>
        <input
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="+447759342229"
          className="w-full bg-[#0a1a14] border border-white/10 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
          data-testid="whatsapp-phone"
        />
      </div>

      {/* Quick templates */}
      <div className="bg-white/5 rounded-lg p-4">
        <p className="text-white/50 text-[11px] uppercase tracking-widest font-semibold mb-2.5">Quick Templates</p>
        <div className="flex flex-wrap gap-2">
          {templates.map((t, i) => (
            <button
              key={i}
              onClick={() => setBody(t.text)}
              className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/25 transition-colors"
              data-testid={`whatsapp-template-${i}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Message body */}
      <div className="bg-white/5 rounded-lg p-4">
        <label className="text-white/50 text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5 mb-2.5">
          <MessageSquare className="w-3.5 h-3.5" /> Message
        </label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={7}
          placeholder="Type your WhatsApp message… *bold*, _italic_, ~strike~ render natively on WhatsApp."
          className="w-full bg-[#0a1a14] border border-white/10 rounded p-3 text-white text-sm leading-relaxed focus:outline-none focus:border-[#D4AF37]/40 resize-none"
          data-testid="whatsapp-body"
        />
        <p className="text-white/30 text-[10px] mt-1.5">{body.length} characters</p>
      </div>

      {/* Send */}
      <div className="flex items-center justify-between">
        <p className="text-white/40 text-[11px]">Message will be logged in this lead's activity feed.</p>
        <Button
          onClick={openInWhatsApp}
          disabled={sending || !body.trim() || !phone.trim()}
          className="bg-[#25D366] text-black hover:bg-[#1FB855] font-semibold"
          data-testid="whatsapp-send-btn"
        >
          <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Opening…' : 'Open in WhatsApp'}
        </Button>
      </div>
    </div>
  );
};

// ===== Documents tab — Cloudinary upload per lead =====
const DocumentsTab = ({ lead, getStoredPassword, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const docs = Array.isArray(lead.documents) ? lead.documents : [];

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large — max 25 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${API_URL}/api/upload/lead-document?submission_id=${lead.id}&password=${getStoredPassword()}`,
        { method: 'POST', body: fd }
      );
      if (res.ok) {
        toast.success('Document uploaded');
        await onChanged();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch {
      toast.error('Network error');
    }
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    if (!confirm('Delete this document?')) return;
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/documents/${docId}?password=${getStoredPassword()}`,
        { method: 'DELETE' }
      );
      if (res.ok) { toast.success('Document removed'); await onChanged(); }
      else toast.error('Delete failed');
    } catch { toast.error('Network error'); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <label
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) uploadFile(e.dataTransfer.files[0]); }}
        className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
          dragOver ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/15 hover:border-[#D4AF37]/40 bg-white/[0.02]'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        data-testid="documents-dropzone"
      >
        <input
          type="file"
          className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
          onChange={e => uploadFile(e.target.files?.[0])}
          data-testid="documents-file-input"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
            <FileText className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <p className="text-white text-sm font-semibold">
            {uploading ? 'Uploading…' : 'Drop a file or click to upload'}
          </p>
          <p className="text-white/40 text-xs">
            PDF, image, DOC, XLS, CSV — up to 25 MB · Stored securely in Cloudinary
          </p>
        </div>
      </label>

      <div className="space-y-2">
        {docs.length === 0 ? (
          <div className="text-center text-white/40 py-6 text-sm italic">
            No documents uploaded yet
          </div>
        ) : (
          docs.map(doc => (
            <div key={doc.id} className="flex items-center gap-3 bg-white/5 rounded-lg p-3" data-testid={`document-${doc.id}`}>
              <div className="w-10 h-10 rounded-md bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{doc.filename}</p>
                <p className="text-white/40 text-xs">
                  {doc.file_type?.toUpperCase()} · {formatSize(doc.file_size)} · {new Date(doc.uploaded_at).toLocaleDateString()}
                </p>
              </div>
              <a
                href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded text-xs font-semibold bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25"
                data-testid={`document-open-${doc.id}`}
              >
                Open
              </a>
              <button
                onClick={() => handleDelete(doc.id)}
                className="p-1.5 rounded text-red-400/70 hover:bg-red-500/15 hover:text-red-400"
                data-testid={`document-delete-${doc.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ===== Pipeline Analytics — collapsible KPI strip + funnel + week chart =====
const PipelineAnalyticsBar = ({ getStoredPassword }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics/pipeline?password=${getStoredPassword()}`);
      if (res.ok) setData(await res.json());
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  React.useEffect(() => { if (open && !data) load(); /* eslint-disable-next-line */ }, [open]);

  const KPI = ({ label, value, sub, accent = '#D4AF37' }) => (
    <div className="bg-white/5 rounded-lg p-3 border border-white/5">
      <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold">{label}</p>
      <p className="text-white font-bold text-xl mt-1" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-white/30 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/[0.02] transition-colors"
        data-testid="analytics-toggle"
      >
        <span className="flex items-center gap-2.5 text-[#D4AF37] font-bold text-sm">
          <BarChart3 className="w-4 h-4" /> Pipeline Analytics
          {data && <span className="text-white/50 text-xs font-normal">— {data.total_leads} leads tracked</span>}
        </span>
        <ChevronRight className={`w-4 h-4 text-white/40 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5">
          {loading || !data ? (
            <div className="py-6 text-center text-white/50 text-sm">Loading analytics…</div>
          ) : (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
                <KPI label="Total Leads" value={data.total_leads} />
                <KPI label="Contact Rate" value={`${data.conv_rates.contact_rate}%`} sub="of new → contacted" accent="#f59e0b" />
                <KPI label="Conversion" value={`${data.conv_rates.conversion_rate}%`} sub="of all leads converted" accent="#22c55e" />
                <KPI label="Hot Leads" value={data.heat_breakdown.hot} sub={`🔥 score ≥ 70`} accent="#ef4444" />
                <KPI label="Avg Days to Contact" value={data.avg_days_to_contact || '—'} accent="#60a5fa" />
                <KPI label="Avg Days to Convert" value={data.avg_days_to_convert || '—'} accent="#a855f7" />
              </div>

              {/* Funnel */}
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2.5">Pipeline Funnel</p>
                <div className="space-y-1.5">
                  {[
                    { label: 'New', value: data.stage_totals.new, color: '#60a5fa' },
                    { label: 'Contacted', value: data.stage_totals.contacted, color: '#f59e0b' },
                    { label: 'Converted', value: data.stage_totals.converted, color: '#22c55e' },
                    { label: 'Closed', value: data.stage_totals.closed, color: '#94a3b8' },
                  ].map(row => {
                    const maxVal = Math.max(1, ...Object.values(data.stage_totals));
                    const pct = Math.round((row.value / maxVal) * 100);
                    return (
                      <div key={row.label} className="flex items-center gap-3">
                        <span className="text-white/50 text-xs w-24 text-right">{row.label}</span>
                        <div className="flex-1 h-6 bg-white/5 rounded-md overflow-hidden">
                          <div className="h-full rounded-md transition-all flex items-center justify-end px-2 text-[10px] font-bold text-black" style={{ width: `${Math.max(pct, 6)}%`, background: row.color }}>
                            {row.value}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* 8-week volume */}
                <div>
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2.5">Lead Volume — Last 8 Weeks</p>
                  <div className="flex items-end gap-1.5 h-24 bg-white/[0.02] rounded-md p-2">
                    {(() => {
                      const maxW = Math.max(1, ...data.weekly_volume.map(w => w.count));
                      return data.weekly_volume.map((w, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                          <div className="w-full bg-[#D4AF37]/70 hover:bg-[#D4AF37] rounded-t transition-all" style={{ height: `${(w.count / maxW) * 75}%`, minHeight: w.count ? '4px' : '2px' }} title={`${w.label}: ${w.count}`} />
                          <span className="text-white/40 text-[9px]">{w.label}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Sources */}
                <div>
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2.5">Sources</p>
                  <div className="space-y-1.5">
                    {Object.entries(data.source_breakdown).map(([src, count]) => {
                      const pct = Math.round((count / Math.max(1, data.total_leads)) * 100);
                      return (
                        <div key={src} className="flex items-center gap-3">
                          <span className="text-white/50 text-xs w-32 capitalize">{src.replace(/_/g, ' ')}</span>
                          <div className="flex-1 h-5 bg-white/5 rounded">
                            <div className="h-full bg-[#D4AF37]/60 rounded transition-all flex items-center justify-end px-2 text-[10px] font-bold text-black" style={{ width: `${Math.max(pct, 6)}%` }}>
                              {count}
                            </div>
                          </div>
                          <span className="text-white/40 text-[10px] w-10 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Top performers */}
              {data.top_performers && data.top_performers.length > 0 && (
                <div>
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2.5">Top Performers</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {data.top_performers.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-white/[0.03] rounded-md px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <span className="text-white text-sm font-medium">{p.name}</span>
                        </div>
                        <span className="text-white/50 text-xs">
                          <span className="text-green-400 font-bold">{p.converted}</span> conv · <span className="text-yellow-400">{p.contacted}</span> contacted · <span className="text-white/60">{p.total}</span> total
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Lead Card Component for Kanban
const LeadCard = ({ lead, onSelect, teamMembers, selectMode, isSelected, onToggleSelect }) => {
  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-400 bg-green-500/20';
    if (score >= 40) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-red-400 bg-red-500/20';
  };

  const assignedMember = teamMembers?.find(m => m.id === lead.assigned_to);
  const hasReminders = lead.reminders?.some(r => !r.completed);

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: lead.id, status: lead.status }));
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.4';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
  };

  return (
    <div 
      draggable={!selectMode}
      onDragStart={selectMode ? undefined : handleDragStart}
      onDragEnd={selectMode ? undefined : handleDragEnd}
      onClick={() => selectMode ? onToggleSelect(lead.id) : onSelect(lead)}
      data-testid={`lead-card-${lead.id}`}
      className={`bg-[#0a1a14] border rounded-lg p-4 transition-all group
                 ${selectMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}
                 ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-white/10 hover:border-[#D4AF37]/30'}`}
    >
      {selectMode && (
        <div className="flex items-center gap-3 mb-2 pb-2 border-b border-white/5">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/30'
          }`}>
            {isSelected && <CheckCircle className="w-3 h-3 text-black" />}
          </div>
          <span className="text-white/40 text-xs">{isSelected ? 'Selected' : 'Click to select'}</span>
        </div>
      )}
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-white font-medium text-sm truncate flex-1">{lead.name}</h4>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {(() => {
            const tier = getHeatTier(lead.lead_score || 0);
            return (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider"
                style={{ backgroundColor: `${tier.color}22`, color: tier.color }}
                title={`${tier.label} · score ${lead.lead_score || 0}`}
              >
                {tier.emoji} {tier.label}
              </span>
            );
          })()}
        </div>
      </div>
      <p className="text-white/50 text-xs mb-2 truncate">{lead.email}</p>
      <p className="text-[#D4AF37] text-xs mb-2">{lead.investment_interest}</p>

      {/* Tags */}
      {Array.isArray(lead.tags) && lead.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2.5">
          {lead.tags.slice(0, 3).map((t, i) => (
            <span
              key={i}
              className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
              style={{ backgroundColor: `${tagColor(t)}22`, color: tagColor(t) }}
            >
              {t}
            </span>
          ))}
          {lead.tags.length > 3 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded text-white/40 bg-white/5">+{lead.tags.length - 3}</span>
          )}
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {assignedMember && (
            <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <User className="w-3 h-3 text-[#D4AF37]" />
            </div>
          )}
          {hasReminders && (
            <Clock className="w-4 h-4 text-yellow-400" />
          )}
          {lead.notes?.length > 0 && (
            <span className="text-white/30 text-xs">{lead.notes.length} notes</span>
          )}
        </div>
        <span className="text-white/30 text-[10px]">
          {new Date(lead.created_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
};

// Lead Detail Modal
const LeadDetailModal = ({ lead, onClose, onUpdate, teamMembers, getStoredPassword, allDeals = [] }) => {
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [newReminder, setNewReminder] = useState({ date: '', note: '' });
  const [activityType, setActivityType] = useState('call');
  const [activityDesc, setActivityDesc] = useState('');
  const [leadData, setLeadData] = useState(lead);
  const [loading, setLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    // Fetch email templates for the Send Email tab
    const fetchTemplates = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/email-templates?password=${getStoredPassword()}`);
        if (res.ok) setEmailTemplates(await res.json());
      } catch (e) {
        console.error('Failed to fetch templates');
      }
    };
    fetchTemplates();
  }, [getStoredPassword]);

  const previewEmailForLead = async (templateId) => {
    setSelectedTemplateId(templateId);
    setEmailPreview(null);
    setEmailSent(false);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${leadData.id}/preview-email?password=${getStoredPassword()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ template_id: templateId })
        }
      );
      if (res.ok) {
        setEmailPreview(await res.json());
      } else {
        toast.error('Failed to load preview');
      }
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const sendEmailToLead = async () => {
    if (!selectedTemplateId) return;
    setSendingEmail(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${leadData.id}/send-email?password=${getStoredPassword()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ template_id: selectedTemplateId })
        }
      );
      if (res.ok) {
        toast.success(`Email sent to ${leadData.email}`);
        setEmailSent(true);
        await refreshLead();
        onUpdate();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    }
    setSendingEmail(false);
  };

  const refreshLead = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/submissions?password=${getStoredPassword()}`);
      if (res.ok) {
        const submissions = await res.json();
        const updated = submissions.find(s => s.id === lead.id);
        if (updated) setLeadData(updated);
      }
    } catch (e) {
      console.error('Failed to refresh lead:', e);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/notes?password=${getStoredPassword()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: newNote })
        }
      );
      if (res.ok) {
        toast.success('Note added');
        setNewNote('');
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to add note');
    }
    setLoading(false);
  };

  const deleteNote = async (noteId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/notes/${noteId}?password=${getStoredPassword()}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('Note deleted');
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to delete note');
    }
  };

  const addReminder = async () => {
    if (!newReminder.date || !newReminder.note) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/reminders?password=${getStoredPassword()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            reminder_date: newReminder.date, 
            note: newReminder.note 
          })
        }
      );
      if (res.ok) {
        toast.success('Reminder added');
        setNewReminder({ date: '', note: '' });
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to add reminder');
    }
    setLoading(false);
  };

  const toggleReminder = async (reminderId, completed) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/reminders/${reminderId}?completed=${!completed}&password=${getStoredPassword()}`,
        { method: 'PATCH' }
      );
      if (res.ok) {
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to update reminder');
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/reminders/${reminderId}?password=${getStoredPassword()}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('Reminder deleted');
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to delete reminder');
    }
  };

  const logActivity = async () => {
    if (!activityDesc.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/activity?activity_type=${activityType}&description=${encodeURIComponent(activityDesc)}&password=${getStoredPassword()}`,
        { method: 'POST' }
      );
      if (res.ok) {
        toast.success('Activity logged');
        setActivityDesc('');
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to log activity');
    }
    setLoading(false);
  };

  const updateLeadScore = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}/score?password=${getStoredPassword()}`,
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`Lead score updated: ${data.lead_score}`);
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to calculate score');
    }
    setLoading(false);
  };

  const updateAssignment = async (teamMemberId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}?password=${getStoredPassword()}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assigned_to: teamMemberId || null })
        }
      );
      if (res.ok) {
        toast.success('Assignment updated');
        await refreshLead();
        onUpdate();
      }
    } catch (e) {
      toast.error('Failed to update assignment');
    }
  };

  const updateTags = async (newTags) => {
    // Optimistic update
    setLeadData(prev => ({ ...prev, tags: newTags }));
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${lead.id}?password=${getStoredPassword()}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: newTags })
        }
      );
      if (res.ok) {
        onUpdate();
      } else {
        toast.error('Failed to save tags');
        await refreshLead();
      }
    } catch (e) {
      toast.error('Network error saving tags');
      await refreshLead();
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email_sent': return <Send className="w-4 h-4 text-blue-400" />;
      case 'call': return <PhoneCall className="w-4 h-4 text-green-400" />;
      case 'meeting': return <Video className="w-4 h-4 text-purple-400" />;
      case 'note_added': return <FileText className="w-4 h-4 text-yellow-400" />;
      default: return <MessageSquare className="w-4 h-4 text-white/40" />;
    }
  };

  const detailTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'email', label: 'Send Email' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'documents', label: 'Documents', count: leadData.documents?.length || 0 },
    { id: 'notes', label: 'Notes', count: leadData.notes?.length || 0 },
    { id: 'reminders', label: 'Reminders', count: leadData.reminders?.filter(r => !r.completed).length || 0 },
    { id: 'activity', label: 'Activity', count: leadData.activities?.length || 0 }
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90" onClick={onClose} />
      <div className="relative bg-[#0d2119] border border-white/10 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <User className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="text-xl text-white font-medium">{leadData.name}</h3>
              <p className="text-white/50 text-sm">{leadData.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`text-2xl font-light ${getScoreColor(leadData.lead_score || 0)}`}>
              {leadData.lead_score || 0}
              <span className="text-xs text-white/30 ml-1">score</span>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded">
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 px-6">
          {detailTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveDetailTab(tab.id)}
              className={`px-4 py-3 text-sm transition-colors relative ${
                activeDetailTab === tab.id 
                  ? 'text-[#D4AF37]' 
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1.5 text-xs bg-white/10 px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
              {activeDetailTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeDetailTab === 'overview' && (
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column - Lead Info */}
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Contact Info</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-white/30" />
                      <span className="text-white text-sm">{leadData.email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Tag className="w-4 h-4 text-white/30" />
                      <span className="text-[#D4AF37] text-sm">{leadData.investment_interest}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Message</h4>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {leadData.message || 'No message provided'}
                  </p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/50">Created</span>
                      <span className="text-white">{new Date(leadData.created_at).toLocaleString()}</span>
                    </div>
                    {leadData.last_contacted && (
                      <div className="flex justify-between">
                        <span className="text-white/50">Last Contacted</span>
                        <span className="text-white">{new Date(leadData.last_contacted).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Actions */}
              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Assignment</h4>
                  <select
                    value={leadData.assigned_to || ''}
                    onChange={(e) => updateAssignment(e.target.value)}
                    className="w-full bg-[#0a1a14] border border-white/10 text-white rounded-md p-2 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers?.map(member => (
                      <option key={member.id} value={member.id}>{member.name}</option>
                    ))}
                  </select>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white/50 text-xs uppercase tracking-wide">Lead Score</h4>
                    <Button 
                      onClick={updateLeadScore} 
                      size="sm" 
                      variant="ghost"
                      className="text-[#D4AF37] text-xs"
                      disabled={loading}
                    >
                      <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                      Recalculate
                    </Button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`text-4xl font-light ${getScoreColor(leadData.lead_score || 0)}`}>
                      {leadData.lead_score || 0}
                    </div>
                    <div className="flex-1">
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            (leadData.lead_score || 0) >= 70 ? 'bg-green-500' :
                            (leadData.lead_score || 0) >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${leadData.lead_score || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5" /> Tags
                  </h4>
                  <TagsEditor
                    tags={Array.isArray(leadData.tags) ? leadData.tags : []}
                    onChange={updateTags}
                  />
                </div>

                <MatchingDealsCard lead={leadData} allDeals={allDeals} />

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Quick Actions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-white/10 text-white/70"
                      onClick={() => { setActivityType('email_sent'); setActiveDetailTab('activity'); }}
                    >
                      <Send className="w-4 h-4 mr-2" /> Log Email
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-white/10 text-white/70"
                      onClick={() => { setActivityType('call'); setActiveDetailTab('activity'); }}
                    >
                      <PhoneCall className="w-4 h-4 mr-2" /> Log Call
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-white/10 text-white/70"
                      onClick={() => setActiveDetailTab('reminders')}
                    >
                      <Calendar className="w-4 h-4 mr-2" /> Set Reminder
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-white/10 text-white/70"
                      onClick={() => setActiveDetailTab('notes')}
                    >
                      <FileText className="w-4 h-4 mr-2" /> Add Note
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Send Email Tab */}
          {activeDetailTab === 'email' && (
            <div className="space-y-5">
              {/* Template Selection */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Select Template</h4>
                <div className="space-y-2">
                  {emailTemplates.filter(t => t.enabled).length === 0 ? (
                    <p className="text-white/40 text-sm">No enabled templates. Go to Email Templates tab to create or enable templates.</p>
                  ) : (
                    emailTemplates.filter(t => t.enabled).map(t => {
                      const typeColors = {
                        auto_reply: 'border-green-500/30 bg-green-500/5',
                        admin_notification: 'border-blue-500/30 bg-blue-500/5',
                        follow_up: 'border-purple-500/30 bg-purple-500/5',
                        custom: 'border-white/10 bg-white/5'
                      };
                      const typeBadge = {
                        auto_reply: 'bg-green-500/20 text-green-400',
                        admin_notification: 'bg-blue-500/20 text-blue-400',
                        follow_up: 'bg-purple-500/20 text-purple-400',
                        custom: 'bg-white/10 text-white/60'
                      };
                      return (
                        <button
                          key={t.id}
                          onClick={() => previewEmailForLead(t.id)}
                          data-testid={`select-template-${t.id}`}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            selectedTemplateId === t.id
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                              : `${typeColors[t.template_type] || typeColors.custom} hover:border-white/20`
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-white text-sm font-medium">{t.name}</span>
                              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${typeBadge[t.template_type] || typeBadge.custom}`}>
                                {t.template_type.replace('_', ' ')}
                              </span>
                            </div>
                            {selectedTemplateId === t.id && (
                              <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                            )}
                          </div>
                          {t.description && <p className="text-white/30 text-xs mt-1">{t.description}</p>}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Email Preview */}
              {emailPreview && (
                <div className="bg-white/5 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <div>
                      <p className="text-white/50 text-xs">To: <span className="text-white">{emailPreview.to_email}</span></p>
                      <p className="text-white/50 text-xs">Subject: <span className="text-white">{emailPreview.subject}</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      {emailSent ? (
                        <span className="text-green-400 text-xs flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <Button
                          onClick={sendEmailToLead}
                          disabled={sendingEmail}
                          className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-8 text-xs px-4"
                          data-testid="send-email-btn"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {sendingEmail ? 'Sending...' : 'Send Email'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="p-1 bg-white max-h-[300px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: emailPreview.html_content }} />
                </div>
              )}

              {!selectedTemplateId && (
                <div className="text-center py-6">
                  <Mail className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">Select a template above to preview and send an email to <span className="text-[#D4AF37]">{leadData.name}</span></p>
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Tab */}
          {activeDetailTab === 'whatsapp' && (
            <WhatsAppTab
              lead={leadData}
              getStoredPassword={getStoredPassword}
              onSent={async () => { await refreshLead(); onUpdate(); }}
            />
          )}

          {/* Documents Tab */}
          {activeDetailTab === 'documents' && (
            <DocumentsTab
              lead={leadData}
              getStoredPassword={getStoredPassword}
              onChanged={async () => { await refreshLead(); onUpdate(); }}
            />
          )}

          {/* Notes Tab */}
          {activeDetailTab === 'notes' && (
            <div className="space-y-4">
              {/* Add Note */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Add Note</h4>
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Write a note about this lead..."
                    className="flex-1 bg-[#0a1a14] border border-white/10 text-white rounded-md p-3 text-sm min-h-[80px] resize-none"
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button 
                    onClick={addNote} 
                    className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
                    disabled={loading || !newNote.trim()}
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Note
                  </Button>
                </div>
              </div>

              {/* Notes List */}
              <div className="space-y-3">
                {(leadData.notes || []).length === 0 ? (
                  <div className="text-center text-white/40 py-8">No notes yet</div>
                ) : (
                  [...(leadData.notes || [])].reverse().map(note => (
                    <div key={note.id} className="bg-white/5 rounded-lg p-4 group">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-white/40 text-xs">
                          {note.created_by} • {new Date(note.created_at).toLocaleString()}
                        </span>
                        <button 
                          onClick={() => deleteNote(note.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded text-red-400"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-white/80 text-sm whitespace-pre-wrap">{note.content}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Reminders Tab */}
          {activeDetailTab === 'reminders' && (
            <div className="space-y-4">
              {/* Add Reminder */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Add Reminder</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/40 text-xs block mb-1">Date & Time</label>
                    <Input
                      type="datetime-local"
                      value={newReminder.date}
                      onChange={(e) => setNewReminder({...newReminder, date: e.target.value})}
                      className="bg-[#0a1a14] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs block mb-1">Note</label>
                    <Input
                      value={newReminder.note}
                      onChange={(e) => setNewReminder({...newReminder, note: e.target.value})}
                      placeholder="Reminder note..."
                      className="bg-[#0a1a14] border-white/10 text-white"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button 
                    onClick={addReminder} 
                    className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
                    disabled={loading || !newReminder.date || !newReminder.note}
                  >
                    <Calendar className="w-4 h-4 mr-2" /> Set Reminder
                  </Button>
                </div>
              </div>

              {/* Reminders List */}
              <div className="space-y-2">
                {(leadData.reminders || []).length === 0 ? (
                  <div className="text-center text-white/40 py-8">No reminders set</div>
                ) : (
                  (leadData.reminders || []).map(reminder => (
                    <div 
                      key={reminder.id} 
                      className={`bg-white/5 rounded-lg p-4 flex items-center gap-4 group ${
                        reminder.completed ? 'opacity-50' : ''
                      }`}
                    >
                      <button 
                        onClick={() => toggleReminder(reminder.id, reminder.completed)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          reminder.completed 
                            ? 'bg-green-500 border-green-500' 
                            : 'border-white/20 hover:border-[#D4AF37]'
                        }`}
                      >
                        {reminder.completed && <CheckCircle className="w-4 h-4 text-white" />}
                      </button>
                      <div className="flex-1">
                        <p className={`text-sm ${reminder.completed ? 'text-white/40 line-through' : 'text-white'}`}>
                          {reminder.note}
                        </p>
                        <p className="text-white/40 text-xs mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(reminder.reminder_date).toLocaleString()}
                        </p>
                      </div>
                      <button 
                        onClick={() => deleteReminder(reminder.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Activity Tab */}
          {activeDetailTab === 'activity' && (
            <div className="space-y-4">
              {/* Log Activity */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Log Activity</h4>
                <div className="flex gap-3">
                  <select
                    value={activityType}
                    onChange={(e) => setActivityType(e.target.value)}
                    className="bg-[#0a1a14] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                  >
                    <option value="call">Phone Call</option>
                    <option value="email_sent">Email Sent</option>
                    <option value="meeting">Meeting</option>
                    <option value="other">Other</option>
                  </select>
                  <Input
                    value={activityDesc}
                    onChange={(e) => setActivityDesc(e.target.value)}
                    placeholder="Activity description..."
                    className="flex-1 bg-[#0a1a14] border-white/10 text-white"
                  />
                  <Button 
                    onClick={logActivity} 
                    className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
                    disabled={loading || !activityDesc.trim()}
                  >
                    Log
                  </Button>
                </div>
              </div>

              {/* Activity Timeline */}
              <div className="space-y-1">
                {(leadData.activities || []).length === 0 ? (
                  <div className="text-center text-white/40 py-8">No activities logged</div>
                ) : (
                  [...(leadData.activities || [])].reverse().map((activity, idx) => (
                    <div key={activity.id} className="flex gap-4 py-3">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                          {getActivityIcon(activity.type)}
                        </div>
                        {idx < (leadData.activities || []).length - 1 && (
                          <div className="w-px h-full bg-white/10 my-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="text-white text-sm">{activity.description}</p>
                        {/* Email engagement badges (populated by SendGrid webhook) */}
                        {activity.type === 'email_sent' && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {activity.delivered_at && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-300 font-semibold" title={`Delivered ${new Date(activity.delivered_at).toLocaleString()}`}>
                                ✓ Delivered
                              </span>
                            )}
                            {activity.opened_at && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 font-semibold" title={`Opened ${new Date(activity.opened_at).toLocaleString()}`}>
                                👁 Opened
                              </span>
                            )}
                            {activity.clicked_at && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] font-semibold" title={`Clicked ${new Date(activity.clicked_at).toLocaleString()}`}>
                                🖱 Clicked
                              </span>
                            )}
                            {activity.bounced_at && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 font-semibold" title={`Bounced ${new Date(activity.bounced_at).toLocaleString()}`}>
                                ⚠ Bounced
                              </span>
                            )}
                          </div>
                        )}
                        <p className="text-white/40 text-xs mt-1">
                          {activity.created_by} • {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main CRM Panel Component
export const CRMPanel = ({ getStoredPassword }) => {
  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [pipeline, setPipeline] = useState({ new: [], contacted: [], converted: [], closed: [] });
  const [submissions, setSubmissions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allDeals, setAllDeals] = useState([]); // Public deals — for Property Matching
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchTemplates, setBatchTemplates] = useState([]);
  const [batchTemplateId, setBatchTemplateId] = useState('');
  const [batchSending, setBatchSending] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pipelineRes, teamRes, remindersRes, dealsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/submissions/pipeline?password=${getStoredPassword()}`),
        fetch(`${API_URL}/api/admin/team?password=${getStoredPassword()}`),
        fetch(`${API_URL}/api/admin/submissions/reminders/upcoming?password=${getStoredPassword()}`),
        fetch(`${API_URL}/api/public/deals`)
      ]);
      
      if (pipelineRes.ok) {
        const data = await pipelineRes.json();
        setPipeline(data);
        // Flatten for list view
        setSubmissions([
          ...data.new, ...data.contacted, ...data.converted, ...data.closed
        ]);
      }
      if (teamRes.ok) setTeamMembers(await teamRes.json());
      if (remindersRes.ok) setUpcomingReminders(await remindersRes.json());
      if (dealsRes.ok) setAllDeals(await dealsRes.json());
    } catch (e) {
      console.error('Failed to fetch CRM data:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const exportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/submissions/export?password=${getStoredPassword()}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded');
      }
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/${leadId}/status?status=${newStatus}&password=${getStoredPassword()}`,
        { method: 'PATCH' }
      );
      if (res.ok) {
        toast.success('Status updated');
        fetchData();
      }
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filteredSubmissions = submissions.filter(sub => 
    sub.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sub.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sub.investment_interest?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleSelectMode = () => {
    if (selectMode) {
      setSelectedIds(new Set());
    }
    setSelectMode(!selectMode);
  };

  const toggleLeadSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllInColumn = (status) => {
    const leads = pipeline[status] || [];
    setSelectedIds(prev => {
      const next = new Set(prev);
      leads.forEach(l => next.add(l.id));
      return next;
    });
  };

  const openBatchEmail = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates?password=${getStoredPassword()}`);
      if (res.ok) {
        const templates = await res.json();
        setBatchTemplates(templates.filter(t => t.enabled));
      }
    } catch (e) {
      console.error('Failed to fetch templates');
    }
    setBatchTemplateId('');
    setBatchResult(null);
    setShowBatchModal(true);
  };

  const sendBatchEmail = async () => {
    if (!batchTemplateId || selectedIds.size === 0) return;
    setBatchSending(true);
    setBatchResult(null);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/submissions/batch-send-email?password=${getStoredPassword()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ template_id: batchTemplateId, submission_ids: [...selectedIds] })
        }
      );
      const data = await res.json();
      if (res.ok) {
        setBatchResult(data);
        toast.success(data.message);
        fetchData();
      } else {
        toast.error(data.detail || 'Batch send failed');
      }
    } catch (e) {
      toast.error('Batch send failed');
    }
    setBatchSending(false);
  };

  const statusConfig = {
    new: { label: 'New Leads', color: 'bg-blue-500', icon: AlertCircle },
    contacted: { label: 'Contacted', color: 'bg-yellow-500', icon: Phone },
    converted: { label: 'Converted', color: 'bg-green-500', icon: CheckCircle },
    closed: { label: 'Closed', color: 'bg-gray-500', icon: X }
  };

  if (loading) {
    return <div className="text-center text-white/50 py-12">Loading CRM data...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">Lead Management</h3>
          <p className="text-white/40 text-sm">
            {submissions.length} total leads • {pipeline.new?.length || 0} new
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Pop Out to fullscreen — only show when not already on /admin/crm */}
          {typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/crm') && (
            <a
              href="/admin/crm"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25 border border-[#D4AF37]/30 transition-all"
              data-testid="crm-popout-btn"
              title="Open CRM in fullscreen on a new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Pop Out
            </a>
          )}
          {/* View Toggle */}
          <div className="bg-white/5 rounded-lg p-1 flex">
            <button
              onClick={() => setView('kanban')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                view === 'kanban' ? 'bg-[#D4AF37] text-black' : 'text-white/60 hover:text-white'
              }`}
            >
              Pipeline
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                view === 'list' ? 'bg-[#D4AF37] text-black' : 'text-white/60 hover:text-white'
              }`}
            >
              List
            </button>
          </div>
          <Button onClick={exportCSV} variant="outline" className="border-white/10 text-white/70">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={toggleSelectMode}
            variant={selectMode ? "default" : "outline"}
            className={selectMode ? "bg-[#D4AF37] text-black hover:bg-[#B8941E]" : "border-white/10 text-white/70"}
            data-testid="select-mode-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" /> {selectMode ? `${selectedIds.size} Selected` : 'Select'}
          </Button>
          <Button onClick={fetchData} variant="ghost" className="text-white/60">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search leads..."
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
      </div>

      {/* Pipeline Analytics (collapsible) */}
      <PipelineAnalyticsBar getStoredPassword={getStoredPassword} />

      {/* Upcoming Reminders Alert */}
      {upcomingReminders.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-medium">
              {upcomingReminders.length} Upcoming Reminder{upcomingReminders.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-1">
            {upcomingReminders.slice(0, 3).map((item, idx) => (
              <div key={idx} className="text-sm text-white/70 flex items-center gap-2">
                <span className="text-[#D4AF37]">{item.lead_name}</span>
                <span className="text-white/40">-</span>
                <span>{item.reminder.note}</span>
                <span className="text-white/30 text-xs ml-auto">
                  {new Date(item.reminder.reminder_date).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kanban View */}
      {view === 'kanban' && (
        <div className="grid grid-cols-4 gap-4" data-testid="kanban-board">
          {Object.entries(statusConfig).map(([status, config]) => {
            const StatusIcon = config.icon;
            const leads = pipeline[status] || [];
            const filteredLeads = searchQuery 
              ? leads.filter(l => 
                  l.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  l.email?.toLowerCase().includes(searchQuery.toLowerCase())
                )
              : leads;
            
            const handleDragOver = (e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDragOverColumn(status);
            };

            const handleDragLeave = (e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setDragOverColumn(null);
              }
            };

            const handleDrop = async (e) => {
              e.preventDefault();
              setDragOverColumn(null);
              try {
                const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                if (data.status !== status) {
                  await updateStatus(data.id, status);
                }
              } catch (err) {
                console.error('Drop error:', err);
              }
            };

            return (
              <div 
                key={status} 
                className={`bg-white/5 rounded-lg p-3 transition-all duration-200 ${
                  dragOverColumn === status 
                    ? 'ring-2 ring-[#D4AF37] bg-[#D4AF37]/5' 
                    : ''
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid={`kanban-column-${status}`}
              >
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
                  <div className={`w-2 h-2 rounded-full ${config.color}`} />
                  <span className="text-white text-sm font-medium">{config.label}</span>
                  <span className="text-white/40 text-xs ml-auto">{filteredLeads.length}</span>
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {selectMode && filteredLeads.length > 0 && (
                    <button 
                      onClick={() => selectAllInColumn(status)}
                      className="w-full text-center text-[#D4AF37] text-xs py-1 hover:bg-[#D4AF37]/10 rounded transition-colors"
                      data-testid={`select-all-${status}`}
                    >
                      Select all {filteredLeads.length}
                    </button>
                  )}
                  {filteredLeads.map(lead => (
                    <LeadCard 
                      key={lead.id} 
                      lead={lead} 
                      onSelect={setSelectedLead}
                      teamMembers={teamMembers}
                      selectMode={selectMode}
                      isSelected={selectedIds.has(lead.id)}
                      onToggleSelect={toggleLeadSelection}
                    />
                  ))}
                  {filteredLeads.length === 0 && (
                    <div className="text-center text-white/30 text-xs py-4">
                      No leads
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="bg-white/5 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {selectMode && <th className="text-left px-4 py-3 w-10"></th>}
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Name</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Interest</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Status</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Score</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Assigned</th>
                <th className="text-left text-white/50 text-xs font-medium px-4 py-3">Date</th>
                <th className="text-right text-white/50 text-xs font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map(lead => {
                const assignedMember = teamMembers?.find(m => m.id === lead.assigned_to);
                return (
                  <tr 
                    key={lead.id} 
                    className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${
                      selectedIds.has(lead.id) ? 'bg-[#D4AF37]/5' : ''
                    }`}
                    onClick={() => selectMode ? toggleLeadSelection(lead.id) : setSelectedLead(lead)}
                  >
                    {selectMode && (
                      <td className="px-4 py-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedIds.has(lead.id) ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/30'
                        }`}>
                          {selectedIds.has(lead.id) && <CheckCircle className="w-3 h-3 text-black" />}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white text-sm">{lead.name}</p>
                        <p className="text-white/40 text-xs">{lead.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#D4AF37] text-sm">{lead.investment_interest}</span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={lead.status}
                        onChange={(e) => { e.stopPropagation(); updateStatus(lead.id, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white/5 border border-white/10 text-white text-xs rounded px-2 py-1"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${
                        (lead.lead_score || 0) >= 70 ? 'text-green-400' :
                        (lead.lead_score || 0) >= 40 ? 'text-yellow-400' : 'text-white/40'
                      }`}>
                        {lead.lead_score || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white/60 text-sm">
                        {assignedMember?.name || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white/40 text-xs">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); }}
                        className="text-white/40 hover:text-[#D4AF37]"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredSubmissions.length === 0 && (
            <div className="text-center text-white/40 py-8">No leads found</div>
          )}
        </div>
      )}

      {/* Floating Action Bar */}
      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#0d2119] border border-[#D4AF37]/30 rounded-xl shadow-2xl shadow-black/50 px-6 py-3 flex items-center gap-4"
             data-testid="batch-action-bar">
          <span className="text-white text-sm">
            <span className="text-[#D4AF37] font-medium">{selectedIds.size}</span> lead{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="w-px h-6 bg-white/10" />
          <Button
            onClick={openBatchEmail}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-9"
            data-testid="batch-send-email-btn"
          >
            <Send className="w-4 h-4 mr-2" /> Send Email
          </Button>
          <Button
            onClick={() => setSelectedIds(new Set())}
            variant="ghost"
            className="text-white/60 h-9"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      )}

      {/* Batch Email Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/90" onClick={() => { setShowBatchModal(false); setBatchResult(null); }} />
          <div className="relative bg-[#0d2119] border border-white/10 rounded-lg w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h3 className="text-lg text-white">Batch Email</h3>
                <p className="text-white/50 text-sm">Send to {selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => { setShowBatchModal(false); setBatchResult(null); }} className="p-2 hover:bg-white/10 rounded">
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {batchResult ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg border ${batchResult.failed === 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className={`w-5 h-5 ${batchResult.failed === 0 ? 'text-green-400' : 'text-yellow-400'}`} />
                      <span className="text-white font-medium">{batchResult.message}</span>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-400">{batchResult.sent} sent</span>
                      {batchResult.failed > 0 && <span className="text-red-400">{batchResult.failed} failed</span>}
                    </div>
                    {batchResult.errors?.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {batchResult.errors.map((err, i) => (
                          <p key={i} className="text-red-400/70 text-xs">{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={() => { setShowBatchModal(false); setBatchResult(null); setSelectMode(false); setSelectedIds(new Set()); }}
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941E]"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <h4 className="text-white/50 text-xs uppercase tracking-wide mb-3">Select Template</h4>
                    {batchTemplates.length === 0 ? (
                      <p className="text-white/40 text-sm">No enabled templates. Create templates in the Email Templates tab.</p>
                    ) : (
                      <div className="space-y-2">
                        {batchTemplates.map(t => {
                          const typeBadge = {
                            auto_reply: 'bg-green-500/20 text-green-400',
                            admin_notification: 'bg-blue-500/20 text-blue-400',
                            follow_up: 'bg-purple-500/20 text-purple-400',
                            custom: 'bg-white/10 text-white/60'
                          };
                          return (
                            <button
                              key={t.id}
                              onClick={() => setBatchTemplateId(t.id)}
                              data-testid={`batch-template-${t.id}`}
                              className={`w-full text-left p-3 rounded-lg border transition-all ${
                                batchTemplateId === t.id
                                  ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                                  : 'border-white/10 bg-white/5 hover:border-white/20'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-white text-sm font-medium">{t.name}</span>
                                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${typeBadge[t.template_type] || typeBadge.custom}`}>
                                    {t.template_type.replace('_', ' ')}
                                  </span>
                                </div>
                                {batchTemplateId === t.id && <CheckCircle className="w-4 h-4 text-[#D4AF37]" />}
                              </div>
                              {t.description && <p className="text-white/30 text-xs mt-1">{t.description}</p>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Selected leads summary */}
                  <div className="bg-white/5 rounded-lg p-3">
                    <h4 className="text-white/50 text-xs uppercase tracking-wide mb-2">Recipients ({selectedIds.size})</h4>
                    <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
                      {submissions.filter(s => selectedIds.has(s.id)).map(s => (
                        <span key={s.id} className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded">
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={sendBatchEmail}
                    disabled={!batchTemplateId || batchSending}
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941E] disabled:opacity-50"
                    data-testid="confirm-batch-send-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {batchSending ? `Sending to ${selectedIds.size} leads...` : `Send to ${selectedIds.size} Lead${selectedIds.size > 1 ? 's' : ''}`}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={fetchData}
          teamMembers={teamMembers}
          getStoredPassword={getStoredPassword}
          allDeals={allDeals}
        />
      )}
    </div>
  );
};

export default CRMPanel;
